/**
 * The app frame, Harvey's (day 33): the dark sidebar and the light workspace.
 *
 * Rendered only inside a SessionContext, which App provides only for a live
 * session. There is no prop or flag that could show it to anyone else.
 *
 * Wider than 860 pixels, the prototype's breakpoint, the sidebar stands in the
 * grid, sticky, and its panel button folds it away; the top bar then offers it
 * back. At 860 and below it is a drawer over the page, in the same order, with
 * a scrim behind it that closes it, and the top bar carries the menu button and
 * the wordmark. A drawer off screen or a sidebar folded away leaves the tab
 * order, so a keyboard cannot walk into links nobody can see. The drawer is
 * fixed and translated, which never adds width, so nothing scrolls sideways.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { expect, http } from "../api";
import { deletionResponseContract } from "../contracts.generated";
import { Button, Icon, Wordmark } from "../ui";
import { Sidebar } from "./Sidebar";
import { useSession } from "./context";
import { useRecents } from "./recents";
import type { RouteId } from "./routes";
import { readTheme, saveTheme, type ThemeChoice } from "./theme";

/** The width at which the sidebar becomes a drawer, the prototype's breakpoint. */
export const DRAWER_QUERY = "(max-width: 860px)";

export function useMedia(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === "undefined" || typeof window.matchMedia !== "function" ? false : window.matchMedia(query).matches,
  );
  useEffect(() => {
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener("change", update);
    return () => list.removeEventListener("change", update);
  }, [query]);
  return matches;
}

export interface ShellProps {
  readonly active: RouteId | null;
  /** Changes on every navigation, which closes the drawer and moves focus. */
  readonly placeKey: string;
  readonly children: ComponentChildren;
}

export function Shell(props: ShellProps) {
  const session = useSession();
  const recents = useRecents(session);
  const drawer = useMedia(DRAWER_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [theme, setTheme] = useState<ThemeChoice>(readTheme);
  const main = useRef<HTMLElement>(null);
  const first = useRef(true);

  // A navigation closes the drawer, and hands focus to the new page so a screen
  // reader starts reading it rather than staying on the link that was pressed.
  // Not on the first render: focus belongs where the browser put it on load.
  // A layout effect, so it lands before the new page paints: after paint, the
  // new page would show for a frame with the drawer still over it.
  useLayoutEffect(() => {
    setDrawerOpen(false);
    if (first.current) {
      first.current = false;
      return;
    }
    main.current?.focus({ preventScroll: true });
  }, [props.placeKey]);

  useEffect(() => {
    if (!drawerOpen) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDrawerOpen(false);
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [drawerOpen]);

  const toggle = () => (drawer ? setDrawerOpen((open) => !open) : setCollapsed((shut) => !shut));
  const sideHidden = drawer ? !drawerOpen : collapsed;

  const classes = ["app"];
  if (drawer && drawerOpen) classes.push("nav-open");
  if (!drawer && collapsed) classes.push("nav-closed");

  return (
    <div class={classes.join(" ")}>
      <Sidebar
        active={props.active}
        profile={session.profile}
        plan={session.plan}
        recents={recents}
        theme={theme}
        hidden={sideHidden}
        onToggle={toggle}
        onFollow={() => setDrawerOpen(false)}
        onTheme={(choice) => {
          saveTheme(choice);
          setTheme(choice);
        }}
        onSignOut={session.signOut}
      />
      {drawer && drawerOpen ? <div class="scrim" aria-hidden="true" onClick={() => setDrawerOpen(false)} /> : null}
      <div class="main">
        <div class="topbar">
          <button
            type="button"
            class="icon-btn topbar-menu"
            aria-label="Open menu"
            title="Open menu"
            aria-controls="sidebar"
            aria-expanded={!sideHidden}
            onClick={toggle}
          >
            <Icon name="panel" />
          </button>
          <Wordmark />
        </div>
        <main class="content" id="content" tabIndex={-1} ref={main}>
          <DeletionNotice />
          {props.children}
        </main>
      </div>
    </div>
  );
}

/**
 * The profile contract says a scheduled deletion is set "so every screen can
 * say so and offer the way back". This is every screen.
 */
function DeletionNotice() {
  const session = useSession();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const deletion = session.profile.deletion;
  if (!deletion) return null;

  const due = new Date(deletion.dueAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  const cancel = async () => {
    setBusy(true);
    setFailed(false);
    try {
      const reply = await http.post("/profile/delete/cancel");
      if (reply.status === 401) {
        session.signOut();
        return;
      }
      expect(reply, deletionResponseContract, "/profile/delete/cancel");
      session.setProfile({ ...session.profile, deletion: null });
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  // A deletion because a plan ended is not the student's to cancel: only a new
  // plan keeps the account (migration 0013), and the API refuses the cancel.
  if (deletion.reason === "plan_ended") {
    return (
      <div class="notice deletion" role="status">
        <span>
          Your plan has ended, and your account is due to be deleted on <b>{due}</b>.
        </span>
        <Button size="sm" href="/settings/data">
          Your data
        </Button>
      </div>
    );
  }

  return (
    <div class="notice deletion" role="status">
      <span>
        Your account is due to be deleted on <b>{due}</b>.
        {failed ? " That did not go through. Try again." : null}
      </span>
      <Button size="sm" disabled={busy} onClick={() => void cancel()}>
        Keep my account
      </Button>
    </div>
  );
}
